import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { grnService } from '../../services/grnService';
import { GRNDTO, GRNStatus, GRNApprovalStatus, GRNType, GRNApprovalStatusDTO, GRNApprovalDTO } from '../../types';
import './GRNPage.css';
import { parseApiError } from '../../utils/errorUtils';
import { Check, X, Clock, ArrowLeft, Printer, CheckCircle2, PenLine, Info } from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_CLASS: Record<GRNStatus, string> = {
  Draft:     'ims-badge ims-badge-draft',
  Received:  'ims-badge ims-badge-received',
  Verified:  'ims-badge ims-badge-verified',
  Completed: 'ims-badge ims-badge-completed',
};

const APPROVAL_CLASS: Record<GRNApprovalStatus, string> = {
  Pending:          'ims-badge ims-badge-pending',
  PendingApproval:  'ims-badge ims-badge-pending',
  PartiallyApproved:'ims-badge ims-badge-partial',
  Approved:         'ims-badge ims-badge-approved',
  Rejected:         'ims-badge ims-badge-rejected',
};

const TYPE_CLASS: Record<GRNType, string> = {
  Direct: 'ims-badge ims-badge-direct',
  WithPO: 'ims-badge ims-badge-withpo',
};

const QUALITY_BADGE: Record<string, string> = {
  Good:      'grn-quality-badge grn-quality-badge--good',
  Partial:   'grn-quality-badge grn-quality-badge--partial',
  Defective: 'grn-quality-badge grn-quality-badge--defective',
};

const STATUS_STEPS: GRNStatus[] = ['Draft', 'Received', 'Verified', 'Completed'];

// ─── Approval level row ────────────────────────────────────────────────────────

interface ApprovalLevelRowProps {
  approval: GRNApprovalDTO;
  currentUserId: number;
  onApprove: (id: number) => void;
  onReject:  (id: number) => void;
  busy: boolean;
  blocked: boolean; // true when a prior level is still pending
}

const ApprovalLevelRow: React.FC<ApprovalLevelRowProps> = ({
  approval, currentUserId, onApprove, onReject, busy, blocked,
}) => {
  const { t } = useTranslation();
  const isAssigned = approval.assignedToUserId === currentUserId;

  const statusIcon = {
    Approved: (
      <span className="grn-level-icon grn-level-icon--approved">
        <Check size={14} strokeWidth={2.5} />
      </span>
    ),
    Rejected: (
      <span className="grn-level-icon grn-level-icon--rejected">
        <X size={14} strokeWidth={2} />
      </span>
    ),
    Pending: (
      <span className="grn-level-icon grn-level-icon--pending">
        <Clock size={14} strokeWidth={2} />
      </span>
    ),
  }[approval.status];

  return (
    <div className={`grn-level-row ${approval.status === 'Pending' && isAssigned ? 'grn-level-row--mine' : ''}`}>
      <div className="grn-level-left">
        {statusIcon}
        <div>
          <div className="grn-level-title">
            {t('grn.approvalLevel')} {approval.approvalLevel}
            {isAssigned && approval.status === 'Pending' && (
              <span className="grn-level-mine-badge">{t('grn.yourApproval')}</span>
            )}
          </div>
          <div className="grn-level-sub">
            {approval.status === 'Approved' && approval.approvingUserName && (
              <>{t('grn.approvedBy')} <strong>{approval.approvingUserName}</strong>
                {approval.approvedAt && ` · ${new Date(approval.approvedAt).toLocaleDateString()}`}
              </>
            )}
            {approval.status === 'Rejected' && (
              <span style={{ color: 'var(--ims-red-text)' }}>
                {t('grn.rejectedBy')} {approval.approvingUserName ?? '—'}
                {approval.rejectionReason && ` — ${approval.rejectionReason}`}
              </span>
            )}
            {approval.status === 'Pending' && (
              <>{t('grn.assignedTo')} <strong>{approval.assignedToUserName ?? `#${approval.assignedToUserId}`}</strong></>
            )}
            {approval.approvalComments && (
              <div style={{ marginTop: 2, color: 'var(--ims-text-sub)', fontStyle: 'italic' }}>
                "{approval.approvalComments}"
              </div>
            )}
          </div>
        </div>
      </div>
      {approval.status === 'Pending' && isAssigned && (
        <div className="grn-level-actions">
          {blocked ? (
            <span style={{ fontSize: 12, color: 'var(--ims-amber-text)', fontWeight: 500 }}>
              {t('grn.awaitingPriorLevel')}
            </span>
          ) : (
            <>
              <button
                className="ims-btn ims-btn-success"
                style={{ fontSize: 12, padding: '4px 10px' }}
                disabled={busy}
                onClick={() => onApprove(approval.id)}
              >
                {t('grn.approve')}
              </button>
              <button
                className="btn-delete"
                style={{ fontSize: 12, padding: '4px 10px' }}
                disabled={busy}
                onClick={() => onReject(approval.id)}
              >
                {t('grn.reject')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const GRNDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate  = useNavigate();
  const location  = useLocation();
  const { t }     = useTranslation();
  const { user }  = useAuth();

  const [grn,            setGrn]            = useState<GRNDTO | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<GRNApprovalStatusDTO | null>(null);
  const [loading,        setLoading]        = useState(true);
  const [busy,           setBusy]           = useState(false);
  const [printBusy,      setPrintBusy]      = useState(false);
  const [completing,     setCompleting]     = useState(false);

  // Rejection modal
  const [showRejectModal,   setShowRejectModal]   = useState(false);
  const [rejectingApprovalId, setRejectingApprovalId] = useState<number | null>(null);
  const [rejectionReason,   setRejectionReason]   = useState('');
  // Approve with comments
  const [showApproveModal,    setShowApproveModal]    = useState(false);
  const [approvingApprovalId, setApprovingApprovalId] = useState<number | null>(null);
  const [approvalComments,    setApprovalComments]    = useState('');

  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { message?: string })?.message ?? null
  );
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [grnRes, appRes] = await Promise.all([
        grnService.getGRNById(id!),
        grnService.getApprovalStatus(id!),
      ]);
      setGrn(grnRes.data?.data ?? null);
      setApprovalStatus(appRes.data?.data ?? null);
    } catch {
      setError(t('grn.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(null), 4500);
      return () => clearTimeout(t);
    }
  }, [successMessage]);

  // ── Actions ──────────────────────────────────────────────────────────────────

  const handleSubmitForApproval = async () => {
    if (!grn || !user) return;
    setBusy(true); setError(null);
    try {
      const res = await grnService.submitForApproval(grn.grnId, user.id);
      setApprovalStatus(res.data?.data ?? approvalStatus);
      const updated = await grnService.getGRNById(grn.grnId);
      setGrn(updated.data?.data ?? grn);
      setSuccessMessage(t('grn.submittedForApproval'));
    } catch (e: unknown) {
      setError(parseApiError(e, t('grn.failedToSubmit')).message);
    } finally {
      setBusy(false);
    }
  };

  const handleApproveLevel = async (approvalId: number) => {
    if (!user) return;
    setBusy(true); setError(null);
    try {
      const res = await grnService.approveGRNLevel(approvalId, user.id, approvalComments || undefined);
      setApprovalStatus(res.data?.data ?? approvalStatus);
      const updated = await grnService.getGRNById(id!);
      setGrn(updated.data?.data ?? grn);
      setShowApproveModal(false); setApprovalComments(''); setApprovingApprovalId(null);
      setSuccessMessage(t('grn.levelApproved'));
    } catch (e: unknown) {
      setError(parseApiError(e, t('grn.failedToApprove')).message);
    } finally {
      setBusy(false);
    }
  };

  const handleRejectLevel = async () => {
    if (!user || !rejectingApprovalId) return;
    if (!rejectionReason.trim()) { setError(t('grn.rejectionReasonRequired')); return; }
    setBusy(true); setError(null);
    try {
      const res = await grnService.rejectGRNLevel(rejectingApprovalId, user.id, rejectionReason);
      setApprovalStatus(res.data?.data ?? approvalStatus);
      const updated = await grnService.getGRNById(id!);
      setGrn(updated.data?.data ?? grn);
      setShowRejectModal(false); setRejectionReason(''); setRejectingApprovalId(null);
      setSuccessMessage(t('grn.levelRejected'));
    } catch (e: unknown) {
      setError(parseApiError(e, t('grn.failedToReject')).message);
    } finally {
      setBusy(false);
    }
  };

  const handleComplete = async () => {
    if (!grn || !user) return;
    setCompleting(true); setError(null);
    try {
      const res = await grnService.completeGRN(grn.grnId, user.id);
      setGrn(res.data?.data ?? grn);
      setSuccessMessage(t('grn.completedSuccess'));
    } catch (e: unknown) {
      setError(parseApiError(e, t('grn.failedToComplete')).message);
    } finally {
      setCompleting(false);
    }
  };

  const handlePrint = async () => {
    if (!grn) return;
    setPrintBusy(true); setError(null);
    try {
      await grnService.openPdfInNewTab(grn.grnId);
    } catch (e: unknown) {
      setError(parseApiError(e, t('grn.failedToPrint')).message);
    } finally {
      setPrintBusy(false);
    }
  };

  // ── Derived flags ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="ims-loading">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!grn) {
    return (
      <Layout>
        <div style={{ padding: '32px 28px' }}>
          <div className="ims-alert ims-alert-error">{error || t('grn.notFound')}</div>
          <button className="btn-cancel" onClick={() => navigate('/grn')}>
            {t('grn.backToList')}
          </button>
        </div>
      </Layout>
    );
  }

  const langCode  = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  const canEdit           = grn.approvalStatus !== 'Approved' && grn.status !== 'Completed';
  // Only show resubmit when previously rejected (new GRNs are auto-submitted on create)
  const canResubmit       = grn.approvalStatus === 'Rejected';
  const canComplete       = grn.approvalStatus === 'Approved' && grn.status !== 'Completed';
  const isPendingApproval = grn.approvalStatus === 'PendingApproval' || grn.approvalStatus === 'PartiallyApproved';

  const totalGood      = grn.grnItems.reduce((s, i) => s + (i.goodQuantity ?? 0), 0);
  const totalDefective = grn.grnItems.reduce((s, i) => s + (i.defectiveQuantity ?? 0), 0);

  const currentStep = STATUS_STEPS.indexOf(grn.status);

  // User's pending approval task (if any)
  const myPendingApproval = approvalStatus?.approvals.find(
    a => a.status === 'Pending' && a.assignedToUserId === user?.id
  );

  return (
    <Layout>
      <div className="grn-detail-page">

        {/* ── Hero ── */}
        <div className="ims-hero">
          <div className="ims-hero-nav">
            <button className="ims-hero-back" onClick={() => navigate('/grn')}>
              <ArrowLeft size={13} strokeWidth={2.5} />
              {t('grn.backToList')}
            </button>
          </div>
          <div className="ims-hero-content">
            <div>
              <h1 className="ims-hero-number">{grn.grnNumber}</h1>
              <p className="ims-hero-supplier">{grn.supplierName || '—'}</p>
              <div className="ims-hero-badges">
                <span className="ims-hero-badge">{t(`grn.status${grn.status}`)}</span>
                <span className="ims-hero-badge">{t(`grn.approval${grn.approvalStatus}`)}</span>
                <span className="ims-hero-badge">
                  {grn.grnType === 'Direct' ? t('grn.typeBadgeDirect') : t('grn.typeBadgeWithPO')}
                </span>
                {grn.totalApprovalLevels > 0 && (
                  <span className="ims-hero-badge">
                    {t('grn.levelProgress', {
                      current: grn.currentApprovalLevel,
                      total:   grn.totalApprovalLevels,
                    })}
                  </span>
                )}
              </div>
            </div>
            <div className="ims-hero-meta">
              <span className="ims-hero-date">{new Date(grn.grnDate).toLocaleDateString(langCode)}</span>
              <span className={STATUS_CLASS[grn.status]}>{t(`grn.status${grn.status}`)}</span>
              <span className={APPROVAL_CLASS[grn.approvalStatus]}>{t(`grn.approval${grn.approvalStatus}`)}</span>
              <span className={TYPE_CLASS[grn.grnType]}>
                {grn.grnType === 'Direct' ? t('grn.typeBadgeDirect') : t('grn.typeBadgeWithPO')}
              </span>
            </div>
          </div>
        </div>

        {/* ── Workflow steps ── */}
        <div className="ims-steps">
          {STATUS_STEPS.map((step, idx) => (
            <React.Fragment key={step}>
              <div className={[
                'ims-step',
                idx < currentStep  ? 'ims-step--done'   : '',
                idx === currentStep ? 'ims-step--active' : '',
              ].join(' ')}>
                <div className="ims-step-dot">{idx < currentStep ? '✓' : idx + 1}</div>
                {t(`grn.status${step}`)}
              </div>
              {idx < STATUS_STEPS.length - 1 && <span className="ims-step-arrow">›</span>}
            </React.Fragment>
          ))}
        </div>

        {/* ── Quality summary ── */}
        <div className="grn-quality-row">
          <div className="grn-quality-card grn-quality-card--good">
            <div className="grn-quality-icon">
              <Check size={16} strokeWidth={2.5} />
            </div>
            <div>
              <span className="grn-quality-qty">{totalGood}</span>
              <span className="grn-quality-label">{t('grn.goodQty')}</span>
            </div>
          </div>
          <div className="grn-quality-card grn-quality-card--defective">
            <div className="grn-quality-icon">
              <X size={16} strokeWidth={2} />
            </div>
            <div>
              <span className="grn-quality-qty">{totalDefective}</span>
              <span className="grn-quality-label">{t('grn.defectiveQty')}</span>
            </div>
          </div>
        </div>

        {/* ── Alerts ── */}
        <div style={{ padding: '16px 28px 0' }}>
          {error && (
            <div className="ims-alert ims-alert-error">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)}
                style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
            </div>
          )}
          {successMessage && (
            <div className="ims-alert ims-alert-success">
              <span>{successMessage}</span>
              <button type="button" onClick={() => setSuccessMessage(null)}
                style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
            </div>
          )}
          {/* My pending task banner */}
          {myPendingApproval && (
            <div className="ims-alert" style={{ background: '#dbeafe', border: '1px solid #93c5fd', color: '#1e40af' }}>
              <Info size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
              <span>
                {t('grn.pendingYourApproval', { level: myPendingApproval.approvalLevel })}
              </span>
            </div>
          )}
        </div>

        {/* ── Main body ── */}
        <div className="ims-detail-body">

          {/* Left / Main */}
          <div className="ims-detail-main">

            {/* GRN Information */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('grn.grnInfo')}</h2>
              </div>
              <div className="ims-info-grid">
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('grn.grnDate')}</span>
                  <span className="ims-info-value">{new Date(grn.grnDate).toLocaleDateString(langCode)}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('grn.receiptDate')}</span>
                  <span className="ims-info-value">{new Date(grn.receiptDate).toLocaleDateString(langCode)}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('grn.supplier')}</span>
                  <span className="ims-info-value">{grn.supplierName || '—'}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('grn.warehouse')}</span>
                  <span className="ims-info-value">{grn.warehouseName || '—'}</span>
                </div>
                {grn.poNumber && (
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('grn.purchaseOrder')}</span>
                    <span className="ims-info-value"><code>{grn.poNumber}</code></span>
                  </div>
                )}
                {grn.referenceNumber && (
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('grn.referenceNumber')}</span>
                    <span className="ims-info-value">{grn.referenceNumber}</span>
                  </div>
                )}
                {grn.paymentTypeName && (
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('grn.paymentType')}</span>
                    <span className="ims-info-value">{grn.paymentTypeName}</span>
                  </div>
                )}
                {grn.notes && (
                  <div className="ims-info-field ims-info-field--full">
                    <span className="ims-info-label">{t('grn.notes')}</span>
                    <span className="ims-info-value" style={{ whiteSpace: 'pre-wrap' }}>{grn.notes}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Approval levels panel */}
            {(approvalStatus?.approvals.length ?? 0) > 0 && (
              <div className="ims-card">
                <div className="ims-card-header">
                  <h2 className="ims-card-title">{t('grn.approvalLevels')}</h2>
                  {approvalStatus && approvalStatus.totalApprovalLevels > 0 && (
                    <span style={{
                      fontSize: 12, fontWeight: 500, color: 'var(--ims-text-sub)',
                      background: 'var(--ims-bg-row-alt)', padding: '2px 8px', borderRadius: 20,
                    }}>
                      {approvalStatus.currentApprovalLevel}/{approvalStatus.totalApprovalLevels} {t('grn.levelsCompleted')}
                    </span>
                  )}
                </div>
                <div className="ims-card-body" style={{ padding: '12px 20px' }}>
                  <div className="grn-levels-list">
                    {approvalStatus!.approvals.map(a => {
                      const priorPending = approvalStatus!.approvals.some(
                        x => x.approvalLevel < a.approvalLevel && x.status !== 'Approved'
                      );
                      return (
                        <ApprovalLevelRow
                          key={a.id}
                          approval={a}
                          currentUserId={user?.id ?? 0}
                          busy={busy}
                          blocked={priorPending}
                          onApprove={(approvalId) => {
                            setApprovingApprovalId(approvalId);
                            setShowApproveModal(true);
                          }}
                          onReject={(approvalId) => {
                            setRejectingApprovalId(approvalId);
                            setShowRejectModal(true);
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Receipt Items */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">
                  {t('grn.receiptItemsCount')}
                  <span style={{ marginInlineStart: 8, fontWeight: 400, color: 'var(--ims-text-muted)', fontSize: 13 }}>
                    ({grn.grnItems.length})
                  </span>
                </h2>
              </div>
              <div className="ims-items-scroll">
                <table className="ims-items-table">
                  <thead>
                    <tr>
                      <th className="ims-items-th">{t('grn.product')}</th>
                      <th className="ims-items-th ims-items-th--right">{t('grn.qtyOrdered')}</th>
                      <th className="ims-items-th ims-items-th--right">{t('grn.qtyReceived')}</th>
                      <th className="ims-items-th">{t('grn.unit')}</th>
                      <th className="ims-items-th">{t('grn.qualityStatus')}</th>
                      <th className="ims-items-th ims-items-th--right" style={{ color: 'var(--ims-green-text)' }}>{t('grn.goodQty')}</th>
                      <th className="ims-items-th ims-items-th--right" style={{ color: 'var(--ims-red-text)' }}>{t('grn.defectiveQty')}</th>
                      <th className="ims-items-th">{t('grn.batchNumber')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grn.grnItems.map((item) => (
                      <tr key={item.grnItemId} className="ims-items-tr">
                        <td className="ims-items-td">
                          <div className="ims-product-name">{item.productName}</div>
                          {item.productCode && <div className="ims-product-code">{item.productCode}</div>}
                        </td>
                        <td className="ims-items-td ims-items-td--right">{item.quantityOrdered}</td>
                        <td className="ims-items-td ims-items-td--right">{item.quantityReceived}</td>
                        <td className="ims-items-td">{item.unitName ?? '—'}</td>
                        <td className="ims-items-td">
                          <span className={QUALITY_BADGE[item.qualityStatus] ?? 'grn-quality-badge grn-quality-badge--good'}>
                            {item.qualityStatus}
                          </span>
                        </td>
                        <td className="ims-items-td ims-items-td--right grn-qty-good">{item.goodQuantity}</td>
                        <td className="ims-items-td ims-items-td--right grn-qty-defective">{item.defectiveQuantity}</td>
                        <td className="ims-items-td">{item.batchNumber || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          </div>

          {/* Sidebar */}
          <div className="ims-detail-sidebar">

            {/* Actions card */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('common.actions')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-actions-list">

                  {/* Print / PDF — always shown */}
                  <button
                    className="ims-action-btn ims-action-btn--print"
                    disabled={printBusy || busy}
                    onClick={handlePrint}
                  >
                    {printBusy ? (
                      <span className="ims-btn-spinner" />
                    ) : (
                      <Printer size={15} strokeWidth={2} />
                    )}
                    {printBusy ? t('grn.generatingPdf') : t('grn.printPdf')}
                  </button>

                  {/* Resubmit for approval — only shown after rejection */}
                  {canResubmit && (
                    <button
                      className="ims-action-btn ims-action-btn--approve"
                      disabled={busy}
                      onClick={handleSubmitForApproval}
                    >
                      {busy ? <span className="ims-btn-spinner" /> : (
                        <CheckCircle2 size={15} strokeWidth={2} />
                      )}
                      {busy ? t('common.loading') : t('grn.resubmitForApproval')}
                    </button>
                  )}

                  {/* Pending / partial approval indicator */}
                  {isPendingApproval && (
                    <div className="grn-pending-banner">
                      <Clock size={14} strokeWidth={2} />
                      {grn.totalApprovalLevels > 0
                        ? t('grn.awaitingLevel', { current: grn.currentApprovalLevel + 1, total: grn.totalApprovalLevels })
                        : t('grn.awaitingApproval')}
                    </div>
                  )}

                  {/* Complete GRN — shown when fully approved */}
                  {canComplete && (
                    <button
                      className="ims-action-btn ims-action-btn--verify"
                      disabled={completing || busy}
                      onClick={handleComplete}
                    >
                      {completing ? <span className="ims-btn-spinner" /> : (
                        <CheckCircle2 size={15} strokeWidth={2} />
                      )}
                      {completing ? t('common.loading') : t('grn.completeGRN')}
                    </button>
                  )}

                  {/* Edit */}
                  {canEdit && (
                    <button
                      className="ims-action-btn ims-action-btn--edit"
                      disabled={busy}
                      onClick={() => navigate(`/grn/${grn.grnId}/edit`)}
                    >
                      <PenLine size={15} strokeWidth={2} />
                      {t('common.edit')}
                    </button>
                  )}

                  <button className="ims-action-btn ims-action-btn--back" onClick={() => navigate('/grn')}>
                    <ArrowLeft size={15} strokeWidth={2} />
                    {t('grn.backToList')}
                  </button>

                </div>
              </div>
            </div>

            {/* Receipt info */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('grn.receivedBy')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-receiver-row">
                  <div className="ims-receiver-avatar">{String(grn.receivedByUserId)[0] ?? '?'}</div>
                  <div>
                    <div className="ims-receiver-name">{t('grn.receivedBy')} #{grn.receivedByUserId}</div>
                    <div className="ims-receiver-email">
                      {new Date(grn.createdDate).toLocaleDateString(langCode)}{' '}
                      {new Date(grn.createdDate).toLocaleTimeString(langCode)}
                    </div>
                  </div>
                </div>
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('grn.grnNumber')}</span>
                  <span className="ims-summary-value"><code style={{ fontSize: 11 }}>{grn.grnNumber}</code></span>
                </div>
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('grn.items')}</span>
                  <span className="ims-summary-value">{grn.grnItems.length}</span>
                </div>
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('grn.createdOn')}</span>
                  <span className="ims-summary-value" style={{ fontSize: 11 }}>
                    {new Date(grn.createdDate).toLocaleDateString(langCode)}
                  </span>
                </div>
                {approvalStatus && approvalStatus.totalValue > 0 && (
                  <div className="ims-summary-row">
                    <span className="ims-summary-label">{t('grn.totalValue')}</span>
                    <span className="ims-summary-value" style={{ fontWeight: 700 }}>
                      {approvalStatus.totalValue.toLocaleString(langCode, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Approve with comments modal ── */}
      {showApproveModal && (
        <div className="ims-modal-overlay">
          <div className="ims-modal">
            <h3 className="ims-modal-title">{t('grn.approveTitle')}</h3>
            <p className="ims-modal-sub">{t('grn.approvalCommentsOptional')}</p>
            <textarea
              value={approvalComments}
              onChange={(e) => setApprovalComments(e.target.value)}
              placeholder={t('grn.approvalCommentsPlaceholder')}
              className="ims-form-control ims-textarea"
              rows={4}
            />
            <div className="ims-modal-footer">
              <button
                type="button"
                className="btn-cancel"
                disabled={busy}
                onClick={() => { setShowApproveModal(false); setApprovalComments(''); setApprovingApprovalId(null); }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="ims-btn ims-btn-success"
                disabled={busy}
                onClick={() => approvingApprovalId && handleApproveLevel(approvingApprovalId)}
              >
                {busy && <span className="ims-btn-spinner" />}
                {t('grn.approveGRN')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject modal ── */}
      {showRejectModal && (
        <div className="ims-modal-overlay">
          <div className="ims-modal">
            <h3 className="ims-modal-title">{t('grn.rejectTitle')}</h3>
            <p className="ims-modal-sub">{t('grn.rejectionReasonRequired')}</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder={t('grn.rejectionReasonPlaceholder')}
              className="ims-form-control ims-textarea"
              rows={5}
            />
            <div className="ims-modal-footer">
              <button
                type="button"
                className="btn-cancel"
                disabled={busy}
                onClick={() => { setShowRejectModal(false); setRejectionReason(''); setRejectingApprovalId(null); }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn-delete"
                disabled={busy}
                onClick={handleRejectLevel}
              >
                {busy && <span className="ims-btn-spinner" />}
                {t('grn.rejectGRN')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default GRNDetailPage;
